import { Component, inject, Input, OnInit } from '@angular/core'
import { GuestService } from '@client/Services/GuestService'
import { Table } from '@ui/Table'
import Guest, {
	STATUS_CONFIRMED,
	STATUS_DECLINED,
	STATUS_PENDING,
} from '@client/Guest'
import HttpError from '@ui/HttpError'
import { PaginatorComponent } from '@ui/Components/Paginator/Paginator.Component'
import { CdkTableComponent } from '@ui/Components/Table/CdkTable.Component'
import Pagination from '@ui/Pagination'
import { createGuest } from '@client/utils'
import { Router } from '@angular/router'
import { RaisedButtonComponent } from '@ui/Components/Form/Button/RaisedButton.Component'
import { MatDialog, MatDialogRef } from '@angular/material/dialog'
import { CreateGuestDialogComponent } from '@client/Dialogs/Guests/CreateGuest.Dialog.Component'
import { NgStyle } from '@angular/common'
import { DebouncedTextFilterComponent } from '@ui/Components/Filters/DebouncedTextFilter.Component'
import { InputValue } from '@ui/Components/Form/TextInput/TextInput.Component'
import { PermissionsService } from '@client/Services/PermissionsService'
import { GuestActionsComponent } from '@client/Components/Guests/Guest/GuestActions.Component'

@Component({
	selector: 'guests-table-component',
	templateUrl: './GuestsTable.Component.html',
	imports: [
		PaginatorComponent,
		CdkTableComponent,
		RaisedButtonComponent,
		NgStyle,
		DebouncedTextFilterComponent,
	],
})
export class GuestsTableComponent extends Table<Guest> implements OnInit {
	@Input() private readonly filter: Guest

	protected readonly guestService: GuestService

	protected readonly permissionsService = inject(PermissionsService)

	private readonly router: Router

	private guests: Guest[] = []

	private createGuestDialogComponentMatDialogRef: MatDialogRef<CreateGuestDialogComponent>

	private readonly matDialog: MatDialog

	constructor() {
		super()
		this.guestService = inject(GuestService)
		this.router = inject(Router)
		this.matDialog = inject(MatDialog)
	}

	public ngOnInit() {
		this.displayColumns = ['name', 'status', 'rsvp']

		this.tableEntities = [
			{
				columnDef: 'name',
				header: 'Name',
				accessorKey: 'name',
			},
			{
				columnDef: 'status',
				header: 'Status',
				accessorCallback(entity: Guest): string | number {
					switch (entity.status) {
						case STATUS_PENDING: {
							return 'Pending'
						}
						case STATUS_CONFIRMED: {
							return 'Confirmed'
						}
						case STATUS_DECLINED: {
							return 'Declined'
						}
						default: {
							return 'Unconfirmed'
						}
					}
				},
				getRowColor(entity: Guest): string {
					switch (entity.status) {
						case STATUS_PENDING: {
							return '#FF7C03'
						}
						case STATUS_CONFIRMED: {
							return '#018F1B'
						}
						case STATUS_DECLINED: {
							return '#FF0000'
						}
						default: {
							return '#000000'
						}
					}
				},
			},
			{
				columnDef: 'rsvp',
				header: 'RSVP',
				type: 'component',
				component: {
					instance: GuestActionsComponent,
					additionalData: {
						guest: (entity: Guest): Guest => entity,
					},
					additionalCallbacks: {
						guestUpdated: async (entity: Guest): Promise<void> => {
							this.guests = this.guests.map((guest: Guest) =>
								guest.id === entity.id ? entity : guest,
							)

							this.setTableData(this.guests)
						},
					},
				},
			},
		]

		if (this.filter) {
			this.guestService.filterable = this.filter
		} else {
			this.guestService.filterable = createGuest()
		}

		this.fetchGuests(true)
	}

	public openCreateGuestDialog = () => {
		this.createGuestDialogComponentMatDialogRef = this.matDialog.open(
			CreateGuestDialogComponent,
		)

		this.createGuestDialogComponentMatDialogRef
			.afterClosed()
			.subscribe(async (guest): Promise<void> => {
				if (guest) {
					this.guests.push(guest)

					this.setTableData(this.guests)

					await this.permissionsService.loadUser()
				}
			})
	}

	public setPaginator = async (paginator: Pagination): Promise<void> => {
		this.guestService.setPaginator(paginator)
		await this.fetchGuests(false)
	}

	public filterGuests = async (text: InputValue): Promise<void> => {
		this.guestService.filterable.name = text as string

		await this.fetchGuests()
	}

	protected goToGuest = (guest: Guest) => {
		this.router.navigateByUrl(`/my-account/guests/${guest.id}`)
	}

	private readonly fetchGuests = async (
		resetPaginator = true,
	): Promise<void> => {
		try {
			if (resetPaginator) {
				this.guestService.paginatorInit(true)
			}

			this.guests = await this.guestService.filter()

			this.setTableData(this.guests)
		} catch (error) {
			if (!(error instanceof HttpError)) {
				throw error
			}
		}
	}
}
